import { useState } from 'react';
import { useTheme, colorSchemes } from '@/context/ThemeContext';
import { ICON_FILTERS } from './constants';

const RING_ORDER = ['food', 'warmth', 'charging', 'water', 'medical', 'shelter', 'community', 'bunker', 'petrol'];
const RING_CATEGORIES = RING_ORDER.map(v => ICON_FILTERS.find(f => f.value === v)).filter(Boolean);
const OVERFLOW_CATEGORIES = ICON_FILTERS.filter(f => !RING_ORDER.includes(f.value));

export const CategoryRing = ({ stats, resourceCategoryFilter, onCategoryClick }) => {
  const [showMore, setShowMore] = useState(false);
  const { colorScheme } = useTheme();
  const theme = colorSchemes[colorScheme];
  const c1 = theme.gradientFrom;
  const c2 = theme.gradientTo;
  const totalResources = stats?.total_resources || 0;
  const ringRadius = 148;
  const angleStep = 360 / RING_CATEGORIES.length;
  const containerSize = 390;
  const center = containerSize / 2;

  return (
    <div className="flex flex-col items-center gap-4" data-testid="category-ring">
      <div className="relative" style={{ width: containerSize, height: containerSize }}>
        {/* Center hub */}
        <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-[120px] h-[120px] rounded-full flex items-center justify-center z-10">
          <div className="absolute inset-0 rounded-full" style={{
            background: 'radial-gradient(circle, hsl(var(--background)) 55%, transparent 100%)',
            boxShadow: `0 0 30px 10px hsl(var(--background) / 0.6), 0 0 60px 20px ${c1}20, 0 0 80px 30px ${c1}10`,
          }} />
          <div className="relative w-[105px] h-[105px] rounded-full flex flex-col items-center justify-center" style={{
            background: `linear-gradient(145deg, ${c1}25, ${c2}20)`,
            border: `2px solid ${c1}66`,
            boxShadow: `inset 0 2px 6px ${c1}33, 0 4px 20px rgba(0,0,0,0.3), 0 0 40px ${c1}18`,
          }}>
            <span className="text-2xl font-black text-foreground leading-none">{totalResources.toLocaleString()}</span>
            <span className="text-[9px] uppercase tracking-wider text-muted-foreground font-semibold mt-0.5">Resources</span>
          </div>
        </div>

        {/* Decorative ring lines */}
        <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-[300px] h-[300px] rounded-full" style={{ border: `1px solid ${c1}25` }} />
        <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-[370px] h-[370px] rounded-full" style={{ border: `1px solid ${c2}18` }} />

        {/* Arc segments */}
        <svg className="absolute inset-0 w-full h-full" viewBox={`0 0 ${containerSize} ${containerSize}`}>
          {RING_CATEGORIES.map((cat, i) => {
            const gapDeg = 16;
            const startAngle = (i * angleStep + gapDeg - 90) * (Math.PI / 180);
            const endAngle = ((i + 1) * angleStep - gapDeg - 90) * (Math.PI / 180);
            const r = 182;
            const x1 = center + r * Math.cos(startAngle);
            const y1 = center + r * Math.sin(startAngle);
            const x2 = center + r * Math.cos(endAngle);
            const y2 = center + r * Math.sin(endAngle);
            const isActive = resourceCategoryFilter === cat.value;
            return (
              <path key={cat.value}
                d={`M ${x1} ${y1} A ${r} ${r} 0 0 1 ${x2} ${y2}`}
                fill="none"
                stroke={isActive ? c1 : `${c1}30`}
                strokeWidth={isActive ? 3 : 1.5}
                strokeLinecap="round"
              />
            );
          })}
        </svg>

        {/* Category nodes */}
        {RING_CATEGORIES.map((cat, i) => {
          const Icon = cat.icon;
          const angle = (i * angleStep - 90) * (Math.PI / 180);
          const x = center + ringRadius * Math.cos(angle);
          const y = center + ringRadius * Math.sin(angle);
          const isActive = resourceCategoryFilter === cat.value;
          const count = stats?.category_counts?.[cat.value] || 0;

          return (
            <button
              key={cat.value}
              onClick={() => onCategoryClick(cat.value)}
              className="absolute flex flex-col items-center gap-0 transition-all duration-200 hover:scale-110 -translate-x-1/2 -translate-y-1/2"
              style={{ left: x, top: y }}
              data-testid={`ring-${cat.value}`}
            >
              <div
                className="w-14 h-14 rounded-full flex items-center justify-center transition-all"
                style={{
                  backgroundColor: isActive ? `${c1}35` : `${c1}18`,
                  border: `2.5px solid ${isActive ? c1 : `${c1}50`}`,
                  boxShadow: isActive ? `0 0 16px ${c1}50, 0 0 32px ${c1}20` : `0 0 8px ${c1}15`,
                }}
              >
                <Icon style={{ color: isActive ? c1 : c2, width: 26, height: 26 }} />
              </div>
              <span className="text-[10px] font-bold mt-0.5 leading-tight text-center max-w-[58px] truncate text-foreground">{cat.label}</span>
              {count > 0 && <span className="text-[10px] font-bold -mt-0.5" style={{ color: c1 }}>{count >= 1000 ? `${(count/1000).toFixed(1)}K` : count}</span>}
            </button>
          );
        })}
      </div>

      {/* Overflow categories */}
      {OVERFLOW_CATEGORIES.length > 0 && (
        <div className="w-full">
          <button
            onClick={() => setShowMore(!showMore)}
            className="text-sm text-foreground/70 hover:text-foreground font-semibold flex items-center gap-1.5 mx-auto transition-colors py-1"
            data-testid="ring-show-more"
          >
            {showMore ? 'Show less' : `+ ${OVERFLOW_CATEGORIES.length} more categories`}
            <svg className={`w-4 h-4 transition-transform ${showMore ? 'rotate-180' : ''}`} fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" /></svg>
          </button>
          {showMore && (
            <div className="flex flex-wrap justify-center gap-2 mt-2 animate-in fade-in slide-in-from-top-2 duration-200">
              {OVERFLOW_CATEGORIES.map(cat => {
                const Icon = cat.icon;
                const isActive = resourceCategoryFilter === cat.value;
                const count = stats?.category_counts?.[cat.value] || 0;
                return (
                  <button
                    key={cat.value}
                    onClick={() => onCategoryClick(cat.value)}
                    className="flex items-center gap-1.5 px-3 py-2 rounded-full border-2 transition-all hover:scale-105"
                    style={{
                      backgroundColor: isActive ? `${c1}25` : 'transparent',
                      borderColor: isActive ? c1 : `${c1}40`,
                    }}
                    data-testid={`ring-overflow-${cat.value}`}
                  >
                    <Icon style={{ color: c1, width: 16, height: 16 }} />
                    <span className="text-xs font-semibold text-foreground">{cat.label}</span>
                    {count > 0 && <span className="text-[11px] font-semibold" style={{ color: c1 }}>{count >= 1000 ? `${(count/1000).toFixed(1)}K` : count}</span>}
                  </button>
                );
              })}
            </div>
          )}
        </div>
      )}
    </div>
  );
};
